import { useEffect, useMemo, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { usePageSeo } from '../lib/seo'
import { API_BASE } from '../lib/api'
import {
  KEYWORD_LANDING_ROUTES,
  getCityBySlug,
  getCityLandingPath,
} from '../lib/seoRoutes'

function buildCityJsonLd(city, faqItems) {
  const url = `https://coeater.com/${city.slug}/find-dining-partner`
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        name: `Find dining partner in ${city.name}`,
        url,
        description: `Find dinning partner near me in ${city.name}. Co-Eater helps you avoid eating alone while on a trip with non-dating social dining meetups.`,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://coeater.com/' },
          { '@type': 'ListItem', position: 2, name: city.name, item: `https://coeater.com/${city.slug}` },
          { '@type': 'ListItem', position: 3, name: 'Find Dining Partner', item: url },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqItems.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      },
    ],
  }
}

const defaultFaqForCity = (cityName) => [
  {
    question: `How do I find a dining partner in ${cityName}?`,
    answer: `Open Co-Eater, set your city to ${cityName}, and browse available members for social lunch or dinner meetups.`,
  },
  {
    question: 'Is Co-Eater a dating app?',
    answer:
      'No. Co-Eater is in the social dining category and is designed for meal companionship, travel dining, and local food meetups.',
  },
]

function normalizeStringList(input) {
  if (!Array.isArray(input)) return []
  return input.map((item) => String(item || '').trim()).filter(Boolean)
}

function normalizeFaqList(input) {
  if (!Array.isArray(input)) return []
  return input
    .map((item) => {
      if (!item || typeof item !== 'object') return null
      const question = String(item.question || '').trim()
      const answer = String(item.answer || '').trim()
      if (!question || !answer) return null
      return { question, answer }
    })
    .filter(Boolean)
}

function normalizeNearbyCities(input, currentCitySlug) {
  if (!Array.isArray(input)) return []
  const seen = new Set()
  return input
    .map((item) => {
      if (!item || typeof item !== 'object') return null
      const name = String(item.name || '').trim()
      const slug = String(item.slug || '').trim().toLowerCase()
      if (!name || !slug || slug === currentCitySlug || seen.has(slug)) return null
      seen.add(slug)
      return { name, slug }
    })
    .filter(Boolean)
}

export function CityDiningLandingPage() {
  const { city: citySlug } = useParams()
  const city = getCityBySlug((citySlug || '').toLowerCase())
  const [cityData, setCityData] = useState(null)

  useEffect(() => {
    if (!city) {
      setCityData(null)
      return
    }

    let cancelled = false

    async function loadCityData() {
      try {
        const res = await fetch(`${API_BASE}/city-landing/${city.slug}`)
        if (!res.ok) {
          if (!cancelled) setCityData(null)
          return
        }

        const payload = await res.json()
        if (!cancelled) setCityData(payload)
      } catch {
        if (!cancelled) setCityData(null)
      }
    }

    loadCityData()
    return () => {
      cancelled = true
    }
  }, [city])

  if (!city) {
    return <Navigate to="/find-dinning-partner-near-me" replace />
  }

  const bestAreasForDining = useMemo(
    () => normalizeStringList(cityData?.bestAreasForDining),
    [cityData?.bestAreasForDining],
  )

  const popularCuisinesInCity = useMemo(
    () => normalizeStringList(cityData?.popularCuisinesInCity),
    [cityData?.popularCuisinesInCity],
  )

  const recommendedMeetupRestaurants = useMemo(
    () => normalizeStringList(cityData?.recommendedMeetupRestaurants),
    [cityData?.recommendedMeetupRestaurants],
  )

  const thingsToRememberInCity = useMemo(
    () => normalizeStringList(cityData?.thingsToRememberInCity),
    [cityData?.thingsToRememberInCity],
  )

  const citySpecificFaq = useMemo(() => {
    const backendFaq = normalizeFaqList(cityData?.citySpecificFaq)
    return backendFaq.length ? backendFaq : defaultFaqForCity(city.name)
  }, [city.name, cityData?.citySpecificFaq])

  const nearbyCities = useMemo(
    () => normalizeNearbyCities(cityData?.nearbyCity, city.slug),
    [city.slug, cityData?.nearbyCity],
  )

  const title = `Find Dining Partner In ${city.name} | Social Dining In ${city.name} | Co-Eater`
  const description = `Find dinning partner near me in ${city.name}. Learn how to avoid eating alone while on a trip and meet people for dinner using a non-dating social dining app.`
  const keywords = [
    `Find dinning partner near me ${city.name}`,
    `find dining partner in ${city.name}`,
    `how to avoid eating alone while on a trip ${city.name}`,
    `non dating apps to meet people on dinner ${city.name}`,
    `${city.name} social dining`,
  ].join(', ')

  usePageSeo({
    title,
    description,
    keywords,
    pathname: getCityLandingPath(city.slug),
    jsonLd: buildCityJsonLd(city, citySpecificFaq),
  })

  return (
    <main className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-100 px-4 py-8 sm:px-8">
      <article className="mx-auto max-w-5xl rounded-3xl border border-orange-100 bg-white/85 p-6 shadow-sm sm:p-10">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600">Social Dining Category</p>
        <h1 className="mt-3 title-font text-4xl font-extrabold text-gray-900 sm:text-5xl">
          Find Dining Partner in {city.name}
        </h1>
        <p className="mt-4 text-base leading-relaxed text-gray-700 sm:text-lg">
          Looking to find dinning partner near me in {city.name}? Co-Eater helps you connect with nearby people for
          lunch and dinner in a non-dating social setting. If you are traveling and want to avoid eating alone while on
          a trip, this page helps you discover meal companions quickly.
        </p>

        <section className="mt-8 grid gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-orange-100 bg-orange-50/60 p-4">
            <h2 className="title-font text-lg font-bold text-gray-900">Step 1</h2>
            <p className="mt-2 text-sm text-gray-700">Open Co-Eater and choose {city.name} as your city.</p>
          </div>
          <div className="rounded-2xl border border-orange-100 bg-orange-50/60 p-4">
            <h2 className="title-font text-lg font-bold text-gray-900">Step 2</h2>
            <p className="mt-2 text-sm text-gray-700">Browse people who are available for lunch or dinner meetups.</p>
          </div>
          <div className="rounded-2xl border border-orange-100 bg-orange-50/60 p-4">
            <h2 className="title-font text-lg font-bold text-gray-900">Step 3</h2>
            <p className="mt-2 text-sm text-gray-700">Send an invite and enjoy social dining without dating pressure.</p>
          </div>
        </section>

        {!!bestAreasForDining.length && (
          <section className="mt-10">
            <h2 className="title-font text-2xl font-bold text-gray-900">Best Areas For Dining</h2>
            <ul className="mt-4 grid gap-3 sm:grid-cols-2">
              {bestAreasForDining.map((item) => (
                <li key={item} className="rounded-xl border border-orange-100 bg-orange-50/40 px-4 py-3 text-sm text-gray-700">
                  {item}
                </li>
              ))}
            </ul>
          </section>
        )}

        {!!popularCuisinesInCity.length && (
          <section className="mt-10">
            <h2 className="title-font text-2xl font-bold text-gray-900">Popular Cuisines In City</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {popularCuisinesInCity.map((item) => (
                <span
                  key={item}
                  className="rounded-full border border-orange-200 bg-white px-3 py-1.5 text-sm font-semibold text-orange-700"
                >
                  {item}
                </span>
              ))}
            </div>
          </section>
        )}

        {!!recommendedMeetupRestaurants.length && (
          <section className="mt-10">
            <h2 className="title-font text-2xl font-bold text-gray-900">Recommended Meetup Restaurants</h2>
            <ul className="mt-4 grid gap-3 sm:grid-cols-2">
              {recommendedMeetupRestaurants.map((item) => (
                <li key={item} className="rounded-xl border border-orange-100 bg-white px-4 py-3 text-sm font-medium text-gray-700">
                  {item}
                </li>
              ))}
            </ul>
          </section>
        )}

        {!!thingsToRememberInCity.length && (
          <section className="mt-10">
            <h2 className="title-font text-2xl font-bold text-gray-900">Things To Remember When You are in this city</h2>
            <ul className="mt-4 space-y-3">
              {thingsToRememberInCity.map((item) => (
                <li key={item} className="rounded-xl border border-orange-100 bg-orange-50/50 px-4 py-3 text-sm text-gray-700">
                  {item}
                </li>
              ))}
            </ul>
          </section>
        )}

        {!!citySpecificFaq.length && (
          <section className="mt-10">
            <h2 className="title-font text-2xl font-bold text-gray-900">City Specifiq FAQ</h2>
            <div className="mt-4 space-y-3">
              {citySpecificFaq.map((item) => (
                <article key={item.question} className="rounded-xl border border-orange-100 bg-white px-4 py-3">
                  <h3 className="text-base font-semibold text-gray-900">{item.question}</h3>
                  <p className="mt-1 text-sm text-gray-700">{item.answer}</p>
                </article>
              ))}
            </div>
          </section>
        )}

        <div className="mt-8 flex flex-wrap gap-3">
          <a
            href="/"
            className="rounded-2xl bg-gradient-to-r from-red-500 to-orange-500 px-6 py-3 text-sm font-bold text-white shadow-lg transition hover:from-red-600 hover:to-orange-600"
          >
            Start Social Dining
          </a>
          <Link
            to="/about"
            className="rounded-2xl border border-orange-200 bg-white px-6 py-3 text-sm font-bold text-orange-700 transition hover:bg-orange-50"
          >
            Why Co-Eater Works
          </Link>
        </div>

        {!!nearbyCities.length && (
          <footer className="mt-10 border-t border-orange-100 pt-8">
            <h2 className="title-font text-2xl font-bold text-gray-900">Nearby City</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 md:grid-cols-3">
              {nearbyCities.map((entry) => (
                <Link
                  key={entry.slug}
                  to={getCityLandingPath(entry.slug)}
                  className="rounded-xl border border-orange-100 bg-white px-4 py-3 text-sm font-semibold text-gray-700 transition hover:border-orange-300 hover:text-orange-700"
                >
                  Find Dining Partner in {entry.name}
                </Link>
              ))}
            </div>
          </footer>
        )}
      </article>
    </main>
  )
}

export function KeywordLandingPage({ keywordPath }) {
  const keywordEntry = KEYWORD_LANDING_ROUTES.find((entry) => entry.path === keywordPath)

  if (!keywordEntry) {
    return <Navigate to="/find-dinning-partner-near-me" replace />
  }

  usePageSeo({
    title: keywordEntry.title,
    description: keywordEntry.description,
    keywords: keywordEntry.keywords.join(', '),
    pathname: keywordEntry.path,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: keywordEntry.heading,
      url: `https://coeater.com${keywordEntry.path}`,
      description: keywordEntry.description,
    },
  })

  return (
    <main className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-100 px-4 py-8 sm:px-8">
      <article className="mx-auto max-w-4xl rounded-3xl border border-orange-100 bg-white/85 p-6 shadow-sm sm:p-10">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600">SEO Landing</p>
        <h1 className="mt-3 title-font text-4xl font-extrabold text-gray-900">{keywordEntry.heading}</h1>
        <p className="mt-4 text-base leading-relaxed text-gray-700 sm:text-lg">{keywordEntry.description}</p>

        <p className="mt-4 text-sm leading-relaxed text-gray-700">
          Co-Eater is built for social dining in India and helps people share meals in a practical, non-dating format.
          Use city pages to find local meal companions and discover dinners without feeling isolated.
        </p>

        <div className="mt-8 flex flex-wrap gap-3">
          <a
            href="/"
            className="rounded-2xl bg-gradient-to-r from-red-500 to-orange-500 px-6 py-3 text-sm font-bold text-white shadow-lg transition hover:from-red-600 hover:to-orange-600"
          >
            Find A Dining Partner
          </a>
          <Link
            to="/about"
            className="rounded-2xl border border-orange-200 bg-white px-6 py-3 text-sm font-bold text-orange-700 transition hover:bg-orange-50"
          >
            Read About Co-Eater
          </Link>
        </div>

        <section className="mt-10">
          <h2 className="title-font text-2xl font-bold text-gray-900">Top City Pages</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {MAJOR_INDIAN_CITIES.slice(0, 10).map((city) => (
              <Link
                key={city.slug}
                to={getCityLandingPath(city.slug)}
                className="rounded-xl border border-orange-100 bg-white px-4 py-3 text-sm font-semibold text-gray-700 transition hover:border-orange-300 hover:text-orange-700"
              >
                {city.name}: Find Dining Partner
              </Link>
            ))}
          </div>
        </section>
      </article>
    </main>
  )
}
